import React, { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { cifraService } from '../lib/supabase'
import { buscarResultados, buscarCifraPorLink, extrairCifraViaProxy, analisarImagemCifra, extrairCifraDePDF, identificarTomRapido, fileParaBase64, gerarCifraPorAudio, gerarCifraPorArtista } from '../lib/ia'
import { usePlano } from '../hooks/usePlano'
import toast from 'react-hot-toast'

const TONS = ['Dó (C)','Dó# (C#)','Ré (D)','Ré# (D#)','Mi (E)','Fá (F)','Fá# (F#)','Sol (G)','Sol# (G#)','Lá (A)','Lá# (A#)','Si (B)']
const GENEROS = ['Gospel','Sertanejo','MPB','Rock','Pop','Forró','Pagode','Samba','Bossa Nova','Worship','Outro']

// ============================================================
// COMPRESSÃO DE IMAGEM — reduz antes de fazer upload residual
// ============================================================
async function comprimirImagem(file, qualidade = 0.75, maxWidth = 1200) {
  return new Promise(resolve => {
    const img = new Image()
    const url = URL.createObjectURL(file)
    img.onload = () => {
      const canvas = document.createElement('canvas')
      const ratio = Math.min(maxWidth / img.width, 1)
      canvas.width = Math.round(img.width * ratio)
      canvas.height = Math.round(img.height * ratio)
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height)
      canvas.toBlob(
        blob => { URL.revokeObjectURL(url); resolve(new File([blob], file.name, { type: 'image/jpeg' })) },
        'image/jpeg', qualidade
      )
    }
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file) } // fallback: arquivo original
    img.src = url
  })
}

// ============================================================
// EXTRAÇÃO REAL DE PDF usando pdf.js (carrega do CDN)
// ============================================================
async function extrairTextoPDF(file) {
  return new Promise(async (resolve, reject) => {
    try {
      // Carrega pdf.js do CDN se ainda não carregou
      if (!window.pdfjsLib) {
        await new Promise((res, rej) => {
          const script = document.createElement('script')
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js'
          script.onload = res
          script.onerror = rej
          document.head.appendChild(script)
        })
        window.pdfjsLib.GlobalWorkerOptions.workerSrc =
          'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js'
      }

      const arrayBuffer = await file.arrayBuffer()
      const pdf = await window.pdfjsLib.getDocument({ data: arrayBuffer }).promise
      let textoCompleto = ''

      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i)
        const content = await page.getTextContent()
        const itens = content.items.filter(it => it.str)

        // Ordena por Y decrescente (PDF: Y cresce para cima) e X crescente
        itens.sort((a, b) => {
          const dy = Math.round(b.transform[5]) - Math.round(a.transform[5])
          return dy !== 0 ? dy : a.transform[4] - b.transform[4]
        })

        // Calcula largura média de caractere a partir dos itens da página
        let somaCharWidth = 0, contCharWidth = 0
        for (const it of itens) {
          if (it.str.length > 0 && it.width > 0) {
            somaCharWidth += it.width / it.str.length
            contCharWidth++
          }
        }
        // Fallback: 6pt por char (10pt Courier) se não conseguiu calcular
        const PT_POR_CHAR = contCharWidth > 0 ? somaCharWidth / contCharWidth : 6

        let linhaAtual = ''
        let yAnterior = null
        let xFinal = 0

        for (const item of itens) {
          const y = Math.round(item.transform[5])
          const x = item.transform[4]

          if (yAnterior !== null && Math.abs(y - yAnterior) > 2) {
            textoCompleto += linhaAtual.trimEnd() + '\n'
            linhaAtual = ''
            xFinal = 0
          }

          // Adiciona espaços proporcionais ao gap horizontal
          if (linhaAtual.length > 0 && x > xFinal + PT_POR_CHAR * 0.5) {
            const espacos = Math.max(1, Math.round((x - xFinal) / PT_POR_CHAR))
            linhaAtual += ' '.repeat(Math.min(espacos, 30))
          }

          linhaAtual += item.str
          // Usa largura real do item se disponível
          xFinal = x + (item.width > 0 ? item.width : item.str.length * PT_POR_CHAR)
          yAnterior = y
        }
        if (linhaAtual.trim()) textoCompleto += linhaAtual.trimEnd() + '\n'
        textoCompleto += '\n'
      }

      resolve(textoCompleto.trim())
    } catch (e) {
      reject(e)
    }
  })
}

// ============================================================
// EXTRAÇÃO REAL DE WORD (.docx) usando mammoth.js
// ============================================================
async function extrairTextoWord(file) {
  return new Promise(async (resolve, reject) => {
    try {
      if (!window.mammoth) {
        await new Promise((res, rej) => {
          const script = document.createElement('script')
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/mammoth/1.6.0/mammoth.browser.min.js'
          script.onload = res
          script.onerror = rej
          document.head.appendChild(script)
        })
      }

      const arrayBuffer = await file.arrayBuffer()
      const result = await window.mammoth.extractRawText({ arrayBuffer })
      resolve(result.value.trim())
    } catch (e) {
      reject(e)
    }
  })
}

// ============================================================
// EXTRAÇÃO REAL DE LINK (Cifra Club e outros)
// ============================================================
async function extrairCifraDeLink(url) {
  // Tenta múltiplos proxies CORS
  const proxies = [
    u => `https://corsproxy.io/?${encodeURIComponent(u)}`,
    u => `https://api.allorigins.win/raw?url=${encodeURIComponent(u)}`,
  ]

  for (const proxy of proxies) {
    try {
      const res = await fetch(proxy(url), { signal: AbortSignal.timeout(10000) })
      if (!res.ok) continue
      const html = await res.text()
      if (!html || html.length < 500) continue

      // Extrai título
      const tituloM = html.match(/<h1[^>]*class="[^"]*t1[^"]*"[^>]*>([^<]+)<\/h1>/i)
        || html.match(/<h1[^>]*>([^<]+)<\/h1>/i)
      const titulo = tituloM?.[1]?.replace(/&amp;/g,'&').trim()

      // Extrai artista
      const artistaM = html.match(/class="[^"]*band-name[^"]*"[^>]*>([^<]+)/i)
        || html.match(/<h2[^>]*>([^<]+)<\/h2>/i)
      const artista = artistaM?.[1]?.replace(/&amp;/g,'&').trim()

      // Tom
      const tomM = html.match(/data-tone="([A-G][#b]?)"/i)
        || html.match(/[Tt]om:\s*(?:<[^>]+>)?([A-G][#b]?)/i)
      const tom = tomM?.[1]

      // Cifra — extrai do <pre> ou área de cifra
      const preM = html.match(/<pre[^>]*>([\s\S]*?)<\/pre>/i)
      const divM = html.match(/id="[^"]*cifra[^"]*"[^>]*>([\s\S]*?)<\/(?:div|section|article)>/i)
      const raw = preM?.[1] || divM?.[1]

      if (raw && raw.length > 50) {
        const cifra = raw
          .replace(/<span[^>]*class="[^"]*acorde[^"]*"[^>]*>([A-G][^<]*)<\/span>/gi, '$1 ')
          .replace(/<[^>]+>/g, '')
          .replace(/&amp;/g,'&').replace(/&lt;/g,'<').replace(/&gt;/g,'>')
          .replace(/&nbsp;/g,' ').replace(/&#39;/g,"'").replace(/&quot;/g,'"')
          .replace(/\r\n/g,'\n').replace(/\r/g,'\n')
          .replace(/\n{3,}/g,'\n\n').trim()

        if (cifra.length > 50) return { titulo, artista, tom, cifra_completa: cifra, fonte: 'link_real' }
      }
    } catch {}
  }
  return null
}

export default function PostarCifraModal({ grupoId, userId, profile, preenchido, onClose, onSuccess }) {
  const navigate = useNavigate()
  const { podeImportar, plano } = usePlano()
  const [etapa, setEtapa] = useState(preenchido ? 2 : 1)
  const [tipo, setTipo] = useState(preenchido ? 'ia' : null)
  const [form, setForm] = useState({
    titulo: preenchido?.titulo || '',
    artista: preenchido?.artista || '',
    genero: preenchido?.genero || '',
    tom_original: preenchido?.tom_original || '',
    tom_ultima_vez: '',
    conteudo_texto: preenchido?.cifra_preview || '',
    link_externo: ''
  })
  const [arquivo, setArquivo] = useState(null)
  const [previewUrl, setPreviewUrl] = useState(null)
  const [buscando, setBuscando] = useState(false)
  const [resultados, setResultados] = useState([])
  const [selecionado, setSelecionado] = useState(null)
  const [carregandoLazy, setCarregandoLazy] = useState(null) // índice do card lazy sendo carregado
  const [mostrarPreview, setMostrarPreview] = useState(false)
  const [analisandoIA, setAnalisandoIA] = useState(false)
  const [salvando, setSalvando] = useState(false)
  const fileRef = useRef()
  const selecionarMusicaRef = useRef(null)

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  // Abas que exigem plano Pro ou Classic (podeImportar)
  const TIPOS = [
    { id: 'ia',     icon: '🤖', label: 'Buscar IA',  premium: false },
    { id: 'audio',  icon: '🎵', label: 'Áudio',      premium: true  },
    { id: 'imagem', icon: '📸', label: 'Foto',        premium: true  },
    { id: 'pdf',    icon: '📄', label: 'PDF',         premium: true  },
    { id: 'word',   icon: '📝', label: 'Word',        premium: true  },
    { id: 'link',   icon: '🔗', label: 'Link',        premium: false },
    { id: 'texto',  icon: '⌨️', label: 'Digitar',    premium: false },
  ]

  async function selecionarTipo(t) {
    // Verifica se o tipo requer plano Pro/Classic
    const tipoConfig = TIPOS.find(tp => tp.id === t)
    if (tipoConfig?.premium && !podeImportar) {
      toast(
        (tst) => (
          <span style={{ display:'flex', alignItems:'center', gap:10 }}>
            <span>🔒 Requer plano <strong>Pro</strong> ou <strong>Classic</strong></span>
            <button
              onClick={() => { toast.dismiss(tst.id); navigate('/planos') }}
              style={{ background:'#FF8C00', border:'none', color:'#fff', padding:'4px 10px', borderRadius:6, cursor:'pointer', fontSize:12, fontWeight:700, flexShrink:0 }}>
              Ver planos
            </button>
          </span>
        ),
        { duration: 5000, icon: '⬆️' }
      )
      return
    }

    setTipo(t)
    if (t === 'pdf') {
      fileRef.current.accept = '.pdf'
      fileRef.current.click()
    } else if (t === 'imagem') {
      fileRef.current.accept = 'image/*'
      fileRef.current.click()
    } else if (t === 'word') {
      fileRef.current.accept = '.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      fileRef.current.click()
    } else if (t === 'audio') {
      fileRef.current.accept = '.mp3,.mp4,.m4a,.wav,.ogg,.webm,.flac,audio/*'
      fileRef.current.click()
    } else {
      setEtapa(2)
    }
  }

  async function onFileChange(e) {
    const file = e.target.files[0]
    if (!file) return
    setArquivo(file)
    setAnalisandoIA(true)

    // ---- IMAGEM ----
    if (tipo === 'imagem') {
      setPreviewUrl(URL.createObjectURL(file))
      try {
        toast('🤖 IA analisando a foto...', { icon: '⏳', duration: 6000 })
        const b64 = await fileParaBase64(file)
        const a = await analisarImagemCifra(b64, file.type)
        if (a.titulo) set('titulo', a.titulo)
        if (a.artista) set('artista', a.artista)
        if (a.tom) set('tom_original', a.tom)
        if (a.cifra_completa?.length > 30) {
          set('conteudo_texto', a.cifra_completa)
          toast.success(`✅ Cifra extraída da foto! ${a.acordes_encontrados?.length||0} acordes`)
        } else {
          toast('⚠️ Foto com qualidade baixa. Preencha o nome e use "Buscar cifra".', { icon:'💡', duration:6000 })
        }
      } catch { toast.error('Erro ao analisar imagem') }
      finally { setAnalisandoIA(false) }
    }

    // ---- PDF — extração REAL de texto ----
    else if (tipo === 'pdf') {
      try {
        toast('📄 Extraindo texto do PDF...', { icon:'⏳', duration:8000 })
        let textoExtraido = ''

        // Etapa 1: tenta extrair texto real com pdf.js
        try {
          textoExtraido = await extrairTextoPDF(file)
          if (textoExtraido.length > 50) {
            toast('🤖 IA identificando cifra no texto...', { icon:'⏳', duration:5000 })
          }
        } catch (e) {
          console.warn('pdf.js falhou:', e.message)
        }

        // Sempre passa pelo Gemini Vision para garantir alinhamento correto dos acordes
        // (texto bruto do pdf.js não preserva colunas de acordes)
        toast('🤖 IA estruturando cifra com alinhamento correto...', { icon:'⏳', duration:10000 })
        const b64 = await fileParaBase64(file)
        const a = await extrairCifraDePDF(b64)
        if (a.titulo) set('titulo', a.titulo)
        if (a.artista) set('artista', a.artista)
        if (a.tom) set('tom_original', a.tom)
        if (a.cifra_completa?.length > 30) {
          set('conteudo_texto', a.cifra_completa)
          toast.success(`✅ Cifra extraída com alinhamento! ${a.acordes_encontrados?.length||0} acordes`)
        } else if (textoExtraido.length > 50) {
          // Fallback: usa texto bruto se Gemini não conseguiu
          const linhas = textoExtraido.split('\n').filter(l => l.trim())
          const possivelTitulo = linhas.find(l => !/^[A-G][#b]?/.test(l.trim()) && l.length > 2 && l.length < 60)
          if (possivelTitulo && !form.titulo) set('titulo', possivelTitulo)
          set('conteudo_texto', textoExtraido)
          toast('⚠️ Gemini não conseguiu estruturar — usando texto bruto. Verifique o alinhamento.', { icon:'💡', duration:7000 })
        } else {
          toast('⚠️ PDF sem texto legível. Preencha o nome e use "Buscar cifra".', { icon:'💡', duration:7000 })
        }
      } catch (err) {
        toast.error('Erro ao processar PDF: ' + err.message)
      } finally { setAnalisandoIA(false) }
    }

    // ---- WORD (.docx) ----
    else if (tipo === 'word') {
      try {
        toast('📝 Extraindo texto do Word...', { icon:'⏳', duration:6000 })
        const texto = await extrairTextoWord(file)
        if (texto.length > 20) {
          const linhas = texto.split('\n').filter(l => l.trim())
          const possivelTitulo = linhas.find(l => l.length > 2 && l.length < 60)
          if (possivelTitulo && !form.titulo) set('titulo', possivelTitulo)
          set('conteudo_texto', texto)
          toast.success(`✅ Word importado! ${linhas.length} linhas extraídas`)
        } else {
          toast('⚠️ Não foi possível extrair texto do Word.', { icon:'💡' })
        }
      } catch (err) {
        toast.error('Erro ao processar Word: ' + err.message)
      } finally { setAnalisandoIA(false) }
    }

    // ---- ÁUDIO (MP3, WAV, M4A, OGG...) ----
    else if (tipo === 'audio') {
      try {
        const MAX_MB = 25
        if (file.size > MAX_MB * 1024 * 1024) {
          toast.error(`Arquivo muito grande. Máximo ${MAX_MB} MB para transcrição.`)
          setAnalisandoIA(false)
          setEtapa(2)
          return
        }

        // SEMPRE parseia título/artista do nome do arquivo (limpa dados de upload anterior)
        // Ex: "Homem do Campo - Banda Louvaner.mp3" → titulo="Homem do Campo" artista="Banda Louvaner"
        let tituloHint = ''
        let artistaHint = ''
        if (file.name) {
          const nomeSemExt = file.name.replace(/\.[^.]+$/, '').trim()
          if (nomeSemExt.includes(' - ')) {
            const partes = nomeSemExt.split(' - ')
            tituloHint = partes[0].trim()
            artistaHint = partes.slice(1).join(' - ').trim()
          } else {
            tituloHint = nomeSemExt
          }
        }
        // Limpa campos do upload anterior e aplica novo título/artista
        set('conteudo_texto', '')
        set('tom_original', '')
        if (tituloHint) set('titulo', tituloHint)
        if (artistaHint) set('artista', artistaHint)

        toast('🎙️ Transcrevendo letra + detectando acordes...', { icon: '⏳', duration: 60000, id: 'audio-loading' })
        const resultado = await gerarCifraPorAudio(
          file,
          tituloHint,
          artistaHint,
          (msg) => toast(msg, { icon: '⏳', duration: 60000, id: 'audio-loading' })
        )
        toast.dismiss('audio-loading')
        // DEBUG — ver exatamente o que voltou da IA
        console.log('[Modal] resultado recebido:', {
          fonte: resultado.fonte,
          titulo: resultado.titulo,
          tom: resultado.tom_original,
          cifra_chars: resultado.cifra_completa?.length ?? 0,
          cifra_preview: (resultado.cifra_completa || '').slice(0, 200)
        })
        // Usa hint local (variáveis capturas antes do await) pois form closure pode estar stale
        const novoTitulo  = resultado.titulo  || tituloHint
        const novoArtista = resultado.artista || artistaHint
        if (novoTitulo)  set('titulo',  novoTitulo)
        if (novoArtista) set('artista', novoArtista)
        if (resultado.tom_original) set('tom_original', resultado.tom_original)
        if (resultado.genero) set('genero', resultado.genero)
        // Sempre seta conteudo_texto — mesmo vazio, para limpar valor anterior
        set('conteudo_texto', resultado.cifra_completa || '')
        if (resultado.cifra_completa) {
          if (resultado.fonte === 'audio_letra_sem_acordes') {
            toast('📝 Letra transcrita! Acordes não detectados pelo Basic Pitch — clique "Buscar cifra" para adicionar pelo nome da música', { icon: '💡', duration: 8000 })
          } else {
            toast.success(`✅ Letra + acordes reais detectados! Revise antes de publicar.`)
          }
        }
      } catch (e) {
        toast.dismiss('audio-loading')
        toast.error('Erro na transcrição: ' + e.message)
      } finally { setAnalisandoIA(false) }
    }

    else { setAnalisandoIA(false) }
    setEtapa(2)
  }

  async function buscar() {
    if (!form.titulo.trim()) { toast.error('Digite o nome ou palavra-chave'); return }
    setBuscando(true); setResultados([]); setSelecionado(null)
    try {
      const lista = await buscarResultados(form.titulo + (form.artista ? ' ' + form.artista : ''))
      if (!lista.length) {
        // Tenta identificar ao menos o tom mesmo sem cifra completa
        const tomInfo = await identificarTomRapido(form.titulo, form.artista)
        if (tomInfo?.tom_original && tomInfo.tom_original !== 'Não identificado') {
          set('tom_original', tomInfo.tom_original)
          toast('Nenhuma cifra completa, mas tom identificado: ' + tomInfo.tom_original, { icon: '🎵', duration: 4000 })
        } else {
          toast.error('Nenhum resultado. Verifique o nome ou tente outro artista.')
        }
      } else {
        setResultados(lista)
        // Sempre exibe para revisão — não auto-seleciona, pois artista/título podem estar errados
        toast.success(`${lista.length} versão(ões) encontrada(s)! Confira o artista antes de selecionar.`)
      }
    } catch (e) { toast.error('Erro na busca: ' + e.message) }
    finally { setBuscando(false) }
  }

  async function selecionarMusica(r, idx) {
    // Card lazy — gera cifra ao clicar (Gemini → Groq → cache)
    if (r._lazy) {
      setCarregandoLazy(idx)
      try {
        toast('⏳ Carregando cifra...', { duration: 8000, id: 'lazy-load' })
        const cifra = await gerarCifraPorArtista(r.titulo, r.artista, r.link_cifraclub)
        toast.dismiss('lazy-load')
        if (cifra?.cifra_completa) {
          selecionarMusica({ ...r, ...cifra, _lazy: false }, idx)
        } else {
          toast.error('Não foi possível carregar a cifra desta versão.')
          setCarregandoLazy(null)
        }
      } catch {
        toast.dismiss('lazy-load')
        toast.error('Erro ao carregar a cifra.')
        setCarregandoLazy(null)
      }
      return
    }
    setCarregandoLazy(null)
    setSelecionado(r)
    set('titulo', r.titulo || '')
    set('artista', r.artista || '')
    set('genero', r.genero || '')
    set('tom_original', r.tom_original || '')
    set('tom_ultima_vez', r.tom_original || '')
    set('conteudo_texto', r.cifra_completa || r.cifra || '')
    if (r.link_cifraclub) set('link_externo', r.link_cifraclub)
    setResultados([])
  }
  // expõe para ser chamada de dentro de buscar()
  selecionarMusicaRef.current = selecionarMusica

  function renderPreview(texto) {
    if (!texto) return null
    return texto.split('\n').map((l, i) => {
      const t = l.trim()
      const isAc = t.length > 0 && /^([A-G][#b]?[mM]?(7|maj7|dim|aug|sus|add)?(\s+|\/[A-G])?)+$/.test(t)
      const isSec = /^(INTRO|VERSO|REFRÃO|PRÉ|PONTE|FINAL|BRIDGE|CODA|PRIMEIRO|SEGUNDO|TERCEIRO)/i.test(t)
      return <div key={i} style={{ color: isSec?'#58a6ff':isAc?'#00CFFF':'rgba(255,255,255,0.8)', fontWeight:isSec||isAc?700:400, fontSize:12, lineHeight:1.6, whiteSpace:'pre' }}>{l||'\u00A0'}</div>
    })
  }

  async function salvar() {
    if (!form.titulo.trim()) { toast.error('Nome da música é obrigatório'); return }
    setSalvando(true)
    try {
      let arquivoUrl = null, arquivoTipo = null
      const textoExtraido = form.conteudo_texto.trim().length > 50

      // Só faz upload se NÃO conseguiu extrair texto (ex: imagem sem texto, PDF corrompido)
      // Se o texto foi extraído com sucesso, o arquivo é descartado — economiza storage
      const MAX_BYTES = 5 * 1024 * 1024 // 5 MB
      if (arquivo && !textoExtraido) {
        if (arquivo.size > MAX_BYTES) {
          toast.error(`Arquivo muito grande (máx 5 MB). Tente outro formato.`)
          setSalvando(false)
          return
        }
        if (tipo === 'imagem') {
          // Comprime antes de enviar — foto de 3MB vira ~200KB
          const comprimida = await comprimirImagem(arquivo)
          arquivoUrl = await cifraService.uploadArquivo(userId, comprimida, tipo)
          arquivoTipo = tipo
        } else if (tipo === 'pdf') {
          arquivoUrl = await cifraService.uploadArquivo(userId, arquivo, tipo)
          arquivoTipo = tipo
        }
      }
      const payload = {
        grupo_id: grupoId,
        autor_id: userId,                          // ← era user_id (bug)
        titulo: form.titulo.trim(),
        artista: form.artista.trim() || null,
        genero: form.genero || null,
        tom_original: form.tom_original || null,
        tom_ultima_vez: form.tom_ultima_vez || form.tom_original || null,
        conteudo_texto: form.conteudo_texto.trim() || null,
        link_externo: form.link_externo.trim() || null,
        arquivo_url: arquivoUrl,
        arquivo_tipo: arquivoTipo || (tipo === 'link' ? 'link' : tipo === 'texto' ? 'texto' : null),
        publicado_em: new Date().toISOString()
      }
      // postarCifra lança erro automaticamente e retorna a cifra criada
      const novaCifra = await cifraService.postarCifra(payload)  // ← era publicarCifra (não existe)
      toast.success('Cifra publicada! 🎸')
      onSuccess?.(novaCifra)   // ← passa a cifra para o feed atualizar sem refresh
      onClose()
    } catch (e) { toast.error('Erro ao salvar: ' + e.message) }
    finally { setSalvando(false) }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-sheet" onClick={e => e.stopPropagation()}>
        <div className="modal-handle" onClick={onClose} />
        <input ref={fileRef} type="file" style={{ display:'none' }} onChange={onFileChange} />

        {/* ETAPA 1 — Escolher tipo */}
        {etapa === 1 && (
          <>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:20 }}>
              <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--muted)', fontSize:20, cursor:'pointer' }}>‹</button>
              <div style={{ fontFamily:'Montserrat,sans-serif', fontSize:18, fontWeight:700 }}>🎵 Compartilhar cifra</div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10 }}>
              {TIPOS.map(t => {
                const bloqueado = t.premium && !podeImportar
                return (
                  <button key={t.id} onClick={() => selecionarTipo(t.id)}
                    style={{
                      padding:'18px 8px', borderRadius:14, cursor:'pointer', textAlign:'center',
                      border: bloqueado ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(255,255,255,0.1)',
                      background: bloqueado ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.04)',
                      position:'relative', overflow:'hidden',
                    }}>
                    <div style={{ fontSize:28, marginBottom:6, opacity: bloqueado ? 0.4 : 1 }}>{t.icon}</div>
                    <div style={{ fontSize:13, fontWeight:600, opacity: bloqueado ? 0.4 : 1, color:'rgba(255,255,255,0.8)' }}>{t.label}</div>
                    {bloqueado && (
                      <div style={{ position:'absolute', top:6, right:6, fontSize:11, lineHeight:1 }}>🔒</div>
                    )}
                  </button>
                )
              })}
            </div>
          </>
        )}

        {/* ETAPA 2 — Formulário */}
        {etapa === 2 && (
          <div style={{ display:'flex', flexDirection:'column', minHeight:0 }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16, flexShrink:0 }}>
              <button onClick={() => setEtapa(1)} style={{ background:'none', border:'none', color:'var(--muted)', fontSize:20, cursor:'pointer' }}>‹</button>
              <div style={{ fontFamily:'Montserrat,sans-serif', fontSize:17, fontWeight:700 }}>
                {tipo === 'ia' ? '🤖 Buscar cifra online' : tipo === 'audio' ? '🎵 Áudio → Cifra' : tipo === 'pdf' ? '📄 PDF' : tipo === 'word' ? '📝 Word' : tipo === 'imagem' ? '📸 Foto' : tipo === 'link' ? '🔗 Link' : '⌨️ Digitar'}
              </div>
            </div>

            <div style={{ flex:1, overflowY:'auto', paddingBottom:16 }}>

              {/* Preview imagem */}
              {previewUrl && (
                <div style={{ marginBottom:12, position:'relative' }}>
                  <img src={previewUrl} alt="" style={{ width:'100%', borderRadius:10, maxHeight:150, objectFit:'contain', background:'#000' }} />
                  {analisandoIA && (
                    <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', background:'rgba(0,0,0,0.8)', borderRadius:10, gap:10 }}>
                      <div className="ai-dots"><div className="ai-dot"/><div className="ai-dot"/><div className="ai-dot"/></div>
                      <div style={{ fontSize:13, color:'var(--gold)', fontWeight:600 }}>IA extraindo cifra...</div>
                    </div>
                  )}
                </div>
              )}

              {/* PDF/Word carregando */}
              {(tipo === 'pdf' || tipo === 'word') && arquivo && analisandoIA && (
                <div style={{ padding:'16px', background:'rgba(0,207,255,0.08)', border:'1px solid rgba(0,207,255,0.2)', borderRadius:10, marginBottom:12, display:'flex', alignItems:'center', gap:10 }}>
                  <div className="ai-dots"><div className="ai-dot"/><div className="ai-dot"/><div className="ai-dot"/></div>
                  <div>
                    <div style={{ fontSize:13, color:'var(--gold)', fontWeight:600 }}>Extraindo texto de {tipo === 'pdf' ? 'PDF' : 'Word'}...</div>
                    <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)' }}>{arquivo.name}</div>
                  </div>
                </div>
              )}

              {/* Dica YouTube → Áudio */}
              {tipo === 'audio' && !arquivo && !analisandoIA && (
                <div style={{ padding:'11px 14px', background:'rgba(255,200,0,0.06)', border:'1px solid rgba(255,200,0,0.18)', borderRadius:10, marginBottom:12, fontSize:12, color:'rgba(255,255,255,0.7)', lineHeight:1.7 }}>
                  <span style={{ color:'#FFD166', fontWeight:700 }}>▶️ Para usar com YouTube:</span> abra <strong style={{color:'#fff'}}>yt1s.com</strong> ou <strong style={{color:'#fff'}}>y2mate.com</strong>, extraia o MP3 e selecione o arquivo aqui.
                </div>
              )}

              {/* Áudio carregando */}
              {tipo === 'audio' && arquivo && analisandoIA && (
                <div style={{ padding:'16px', background:'rgba(0,207,255,0.08)', border:'1px solid rgba(0,207,255,0.2)', borderRadius:10, marginBottom:12, display:'flex', alignItems:'center', gap:12 }}>
                  <div className="ai-dots"><div className="ai-dot"/><div className="ai-dot"/><div className="ai-dot"/></div>
                  <div>
                    <div style={{ fontSize:13, color:'var(--gold)', fontWeight:600 }}>Whisper transcrevendo o áudio...</div>
                    <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)' }}>{arquivo.name} · pode levar até 1 minuto</div>
                  </div>
                </div>
              )}

              {/* Áudio confirmado */}
              {tipo === 'audio' && arquivo && !analisandoIA && (
                <div style={{ padding:'12px 14px', background:'rgba(0,207,255,0.07)', border:'1px solid rgba(0,207,255,0.2)', borderRadius:10, marginBottom:12, display:'flex', alignItems:'center', gap:10 }}>
                  <span style={{ fontSize:22 }}>🎵</span>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:600, fontSize:13, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{arquivo.name}</div>
                    <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)' }}>{(arquivo.size/1024/1024).toFixed(1)} MB</div>
                  </div>
                  {form.conteudo_texto && <span style={{ fontSize:11, color:'#3fb950', fontWeight:600 }}>✓ Letra transcrita</span>}
                </div>
              )}

              {/* PDF/Word confirmado */}
              {(tipo === 'pdf' || tipo === 'word') && arquivo && !analisandoIA && (
                <div style={{ padding:'12px 14px', background:'rgba(255,140,0,0.07)', border:'1px solid rgba(255,140,0,0.2)', borderRadius:10, marginBottom:12, display:'flex', alignItems:'center', gap:10 }}>
                  <span style={{ fontSize:22 }}>{tipo === 'word' ? '📝' : '📄'}</span>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:600, fontSize:13, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{arquivo.name}</div>
                    <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)' }}>{(arquivo.size/1024).toFixed(0)} KB</div>
                  </div>
                  {form.conteudo_texto && <span style={{ fontSize:11, color:'#3fb950', fontWeight:600 }}>✓ Texto extraído</span>}
                </div>
              )}

              {/* Campo busca / título */}
              <div className="input-group">
                <label className="input-label">
                  {tipo === 'ia' && !selecionado ? 'Nome, artista ou trecho da letra' : 'Título da música *'}
                </label>
                <div style={{ display:'flex', gap:8 }}>
                  <input className="input" style={{ flex:1 }}
                    placeholder={tipo === 'ia' && !selecionado ? 'Ex: sacramento da comunhão, jesus filho de davi...' : 'Nome da música'}
                    value={form.titulo} onChange={e => set('titulo', e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && buscar()} autoFocus />
                  {(tipo === 'ia' || ((tipo === 'pdf' || tipo === 'word' || tipo === 'imagem' || tipo === 'audio') && !form.conteudo_texto && !analisandoIA)) && (
                    <button className="btn" onClick={buscar} disabled={buscando}
                      style={{ borderColor:'var(--gold)', color:'var(--gold)', flexShrink:0, fontSize:11, padding:'8px 10px' }}>
                      {buscando ? <span className="spinner" style={{ width:14, height:14 }}/> : '🔍 Buscar cifra'}
                    </button>
                  )}
                </div>
                {(tipo === 'pdf' || tipo === 'word' || tipo === 'imagem' || tipo === 'audio') && !form.conteudo_texto && !analisandoIA && (
                  <div style={{ fontSize:11, color:'var(--gold)', marginTop:6, padding:'8px 10px', background:'rgba(0,207,255,0.08)', borderRadius:8 }}>
                    💡 Digite o nome e clique "Buscar cifra" para baixar de bases públicas
                  </div>
                )}
              </div>

              {/* Resultados da busca */}
              {resultados.length > 0 && (
                <div style={{ marginBottom:14 }}>
                  <div style={{ fontSize:11, color:'var(--gold)', marginBottom:8, fontWeight:700, textTransform:'uppercase' }}>
                    {resultados.length} versão(ões) encontrada(s) — escolha:
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', gap:6, maxHeight:280, overflowY:'auto' }}>
                    {resultados.map((r, i) => {
                      const isCarregando = carregandoLazy === i
                      return (
                        <div key={i} onClick={() => !isCarregando && selecionarMusica(r, i)}
                          style={{ padding:'12px 14px', background: isCarregando ? 'rgba(0,207,255,0.08)' : 'rgba(255,255,255,0.04)', border:`1px solid ${isCarregando ? 'rgba(0,207,255,0.3)' : 'rgba(255,255,255,0.1)'}`, borderRadius:12, cursor: isCarregando ? 'wait' : 'pointer', display:'flex', alignItems:'center', gap:12, opacity: carregandoLazy !== null && !isCarregando ? 0.5 : 1 }}>
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ fontWeight:700, fontSize:14 }}>{r.titulo}</div>
                            <div style={{ fontSize:12, color:'var(--muted)' }}>{r.artista}{r.genero ? ` · ${r.genero}` : ''}</div>
                          </div>
                          <div style={{ textAlign:'right', flexShrink:0 }}>
                            {r.tom_original
                              ? <div style={{ fontSize:12, color:'var(--gold)', fontWeight:700, background:'rgba(0,207,255,0.1)', padding:'3px 8px', borderRadius:6 }}>{r.tom_original}</div>
                              : r._lazy && <div style={{ fontSize:11, color:'var(--muted)', padding:'3px 8px' }}>📥 Clique</div>
                            }
                          </div>
                          <span style={{ color:'var(--muted)', fontSize:16 }}>
                            {isCarregando ? '⏳' : '›'}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Música selecionada */}
              {selecionado && (
                <div style={{ padding:'12px 14px', background:'rgba(0,207,255,0.07)', border:'1px solid rgba(0,207,255,0.2)', borderRadius:12, marginBottom:12 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom: mostrarPreview && form.conteudo_texto ? 10 : 0 }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:700 }}>✓ {selecionado.titulo}</div>
                      <div style={{ fontSize:12, color:'var(--muted)' }}>{selecionado.artista} · Tom: <span style={{ color:'var(--gold)', fontWeight:700 }}>{selecionado.tom_original}</span></div>
                    </div>
                    <button onClick={() => setMostrarPreview(p=>!p)} style={{ background:'rgba(255,255,255,0.08)', border:'none', color:'var(--gold)', padding:'5px 10px', borderRadius:8, cursor:'pointer', fontSize:12, fontWeight:600 }}>
                      {mostrarPreview ? '▲ Ocultar' : '👁 Ver cifra'}
                    </button>
                    <button onClick={() => { setSelecionado(null); set('titulo',''); set('conteudo_texto','') }} style={{ background:'none', border:'none', color:'var(--muted)', fontSize:20, cursor:'pointer' }}>×</button>
                  </div>
                  {mostrarPreview && form.conteudo_texto && (
                    <div style={{ background:'rgba(0,0,0,0.3)', borderRadius:10, padding:'12px', fontFamily:"'Courier New',monospace", maxHeight:200, overflowY:'auto' }}>
                      {renderPreview(form.conteudo_texto)}
                    </div>
                  )}
                  {selecionado.link_cifraclub && (
                    <a href={selecionado.link_cifraclub} target="_blank" rel="noreferrer" style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:11, color:'rgba(0,207,255,0.7)', textDecoration:'none', marginTop:8 }}>
                      🔗 Ver fonte original
                    </a>
                  )}
                </div>
              )}

              {/* Artista + Gênero — sempre visíveis e editáveis em todos os tipos */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <div className="input-group">
                  <label className="input-label">Artista</label>
                  <input className="input" placeholder="Ex: Fernandinho" value={form.artista} onChange={e => set('artista', e.target.value)} />
                </div>
                <div className="input-group">
                  <label className="input-label">Gênero</label>
                  <select className="input" value={form.genero} onChange={e => set('genero', e.target.value)} style={{ appearance:'none' }}>
                    <option value="">Selecionar</option>
                    {GENEROS.map(g => <option key={g}>{g}</option>)}
                  </select>
                </div>
              </div>

              {/* Link */}
              {tipo === 'link' && (
                <div className="input-group">
                  <label className="input-label">Link da cifra</label>
                  <div style={{ display:'flex', gap:8 }}>
                    <input className="input" style={{ flex:1 }} type="url"
                      placeholder="https://www.cifraclub.com.br/... ou qualquer site de cifras"
                      value={form.link_externo} onChange={e => set('link_externo', e.target.value)} />
                    <button className="btn" disabled={buscando || !form.link_externo}
                      onClick={async () => {
                        if (!form.link_externo) return
                        setBuscando(true)
                        const toastId = toast.loading('🤖 Extraindo cifra do link...')
                        try {
                          // 1. Tenta extração server-side (com proxies anti-bot)
                          toast.loading('🔍 Tentando extrair do site...', { id: toastId })
                          const resp = await fetch(`/api/cifra-extract?url=${encodeURIComponent(form.link_externo)}`, {
                            signal: AbortSignal.timeout(45000)
                          })
                          const r = await resp.json()

                          if (r?.cifra_completa?.length > 30) {
                            if (r.titulo) set('titulo', r.titulo)
                            if (r.artista) set('artista', r.artista)
                            if (r.tom_original) set('tom_original', r.tom_original)
                            set('conteudo_texto', r.cifra_completa)
                            toast.success(r.aviso ? `✅ ${r.aviso}` : '✅ Cifra extraída com sucesso!', { id: toastId, duration: 5000 })
                            return
                          }

                          // Preenche campos com dados da URL mesmo sem cifra
                          if (r?.titulo) set('titulo', r.titulo)
                          if (r?.artista) set('artista', r.artista)

                          // 2. Fallback: proxy client-side
                          toast.loading('🔄 Tentando via proxy...', { id: toastId })
                          const fallback = await buscarCifraPorLink(form.link_externo)
                          if (fallback?.cifra_completa?.length > 30) {
                            if (fallback.titulo) set('titulo', fallback.titulo)
                            if (fallback.artista) set('artista', fallback.artista)
                            if (fallback.tom_original) set('tom_original', fallback.tom_original)
                            set('conteudo_texto', fallback.cifra_completa)
                            toast.success('✅ Cifra extraída via proxy!', { id: toastId })
                            return
                          }

                          // 3. Fallback final: busca por nome+artista extraídos da URL
                          const tituloUrl = r?.titulo || form.titulo
                          const artistaUrl = r?.artista || form.artista
                          if (r?.podeBuscarPorNome && tituloUrl && artistaUrl) {
                            toast.loading(`🤖 Site bloqueou acesso — buscando "${tituloUrl}" por nome...`, { id: toastId })
                            const busca = await buscarResultados(`${tituloUrl} ${artistaUrl}`)
                            if (busca?.length > 0 && busca[0]?.cifra_completa?.length > 30) {
                              const melhor = busca[0]
                              if (melhor.titulo) set('titulo', melhor.titulo)
                              if (melhor.artista) set('artista', melhor.artista)
                              if (melhor.tom_original) set('tom_original', melhor.tom_original)
                              set('conteudo_texto', melhor.cifra_completa)
                              toast.success('✅ Cifra encontrada pela busca!', { id: toastId, duration: 5000 })
                              return
                            }
                          }

                          // Nada funcionou — link salvo, cifra não extraída
                          toast('🔗 Link salvo. O site bloqueou a extração automática.\nUse "Buscar IA" digitando o nome, ou cole a cifra manualmente.', {
                            id: toastId, icon: '⚠️', duration: 7000
                          })
                        } catch (e) {
                          toast.error('Erro ao processar link: ' + (e.message || ''), { id: toastId })
                        }
                        finally { setBuscando(false) }
                      }}
                      style={{ borderColor:'var(--gold)', color:'var(--gold)', flexShrink:0, fontSize:11, padding:'8px 10px' }}>
                      {buscando ? <span className="spinner" style={{ width:14, height:14 }}/> : '🤖 Extrair'}
                    </button>
                  </div>
                  <div style={{ fontSize:11, color:'var(--muted)', marginTop:4 }}>
                    Cole o link e clique "Extrair" para baixar a cifra completa
                  </div>
                  {form.conteudo_texto && tipo === 'link' && (
                    <div style={{ marginTop:10 }}>
                      <label className="input-label">Cifra extraída ✓</label>
                      <textarea className="input" rows={7} value={form.conteudo_texto}
                        onChange={e => set('conteudo_texto', e.target.value)}
                        style={{ fontFamily:"'Courier New',monospace", fontSize:12, lineHeight:1.6, background:'rgba(63,185,80,0.05)', borderColor:'rgba(63,185,80,0.3)', resize:'vertical' }} />
                      <div style={{ fontSize:11, color:'#3fb950', marginTop:4 }}>✅ Pode transpor o tom após publicar</div>
                    </div>
                  )}
                </div>
              )}

              {/* YouTube */}
              {tipo === 'youtube' && (
                <div className="input-group">
                  <label className="input-label">Link do YouTube</label>
                  <div style={{ display:'flex', gap:8 }}>
                    <input className="input" style={{ flex:1 }} type="url"
                      placeholder="https://youtube.com/watch?v=..."
                      value={youtubeUrl}
                      onChange={e => setYoutubeUrl(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && !analisandoIA && gerarDoYoutube()}
                      autoFocus />
                    <button className="btn" onClick={gerarDoYoutube} disabled={analisandoIA || !youtubeUrl.trim()}
                      style={{ borderColor:'#FF0000', color:'#FF0000', flexShrink:0, fontSize:11, padding:'8px 10px', opacity: analisandoIA ? 0.6 : 1 }}>
                      {analisandoIA
                        ? <span className="spinner" style={{ width:14, height:14 }}/>
                        : '🎵 Gerar'}
                    </button>
                  </div>
                  <div style={{ fontSize:11, color:'var(--muted)', marginTop:4 }}>
                    Cole o link e clique "Gerar" — a IA ouve, transcreve a letra e detecta os acordes
                  </div>

                  {/* Carregando YouTube */}
                  {analisandoIA && (
                    <div style={{ marginTop:12, padding:'16px', background:'rgba(255,0,0,0.06)', border:'1px solid rgba(255,0,0,0.2)', borderRadius:10, display:'flex', alignItems:'center', gap:12 }}>
                      <div className="ai-dots"><div className="ai-dot"/><div className="ai-dot"/><div className="ai-dot"/></div>
                      <div>
                        <div style={{ fontSize:13, color:'var(--gold)', fontWeight:600 }}>IA ouvindo a música...</div>
                        <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)' }}>Pode levar até 30 segundos</div>
                      </div>
                    </div>
                  )}

                  {/* Cifra gerada */}
                  {!analisandoIA && form.conteudo_texto && tipo === 'youtube' && (
                    <div style={{ marginTop:12 }}>
                      <label className="input-label" style={{ display:'flex', justifyContent:'space-between' }}>
                        <span>Cifra gerada ✓</span>
                        <span style={{ color:'#3fb950', fontSize:10, fontWeight:400 }}>Pode editar</span>
                      </label>
                      <textarea className="input" rows={9} value={form.conteudo_texto}
                        onChange={e => set('conteudo_texto', e.target.value)}
                        style={{ fontFamily:"'Courier New',monospace", fontSize:12, lineHeight:1.6,
                          background:'rgba(63,185,80,0.05)', borderColor:'rgba(63,185,80,0.3)', resize:'vertical' }} />
                      <div style={{ fontSize:11, color:'#3fb950', marginTop:4 }}>✅ Revise a cifra e ajuste se necessário antes de publicar</div>
                    </div>
                  )}
                </div>
              )}

              {/* Texto manual */}
              {tipo === 'texto' && (
                <div className="input-group">
                  <label className="input-label">Cifra</label>
                  <textarea className="input" rows={8}
                    placeholder={'G         D\nPrimeira linha...\nEm        C\nSegunda linha...'}
                    value={form.conteudo_texto} onChange={e => set('conteudo_texto', e.target.value)}
                    style={{ resize:'vertical', fontFamily:"'Courier New',monospace", fontSize:13, lineHeight:1.6 }} />
                </div>
              )}

              {/* Cifra extraída (PDF/Word/Foto/Áudio) */}
              {(tipo === 'pdf' || tipo === 'word' || tipo === 'imagem' || tipo === 'audio') && !analisandoIA && (
                <div className="input-group">
                  <label className="input-label" style={{ display:'flex', justifyContent:'space-between' }}>
                    <span>Cifra extraída {form.conteudo_texto ? '✓' : '(vazio)'}</span>
                    {form.conteudo_texto && <span style={{ color:'#3fb950', fontSize:10, fontWeight:400 }}>Pode editar</span>}
                  </label>
                  <textarea className="input" rows={9}
                    placeholder={'A IA extrai automaticamente.\nSe não extraiu, preencha o nome acima e clique "Buscar cifra".\n\nOu digite manualmente:\nG         D\nPrimeira linha...'}
                    value={form.conteudo_texto} onChange={e => set('conteudo_texto', e.target.value)}
                    style={{ resize:'vertical', fontFamily:"'Courier New',monospace", fontSize:12, lineHeight:1.6,
                      background: form.conteudo_texto ? 'rgba(63,185,80,0.05)' : 'rgba(255,255,255,0.06)',
                      borderColor: form.conteudo_texto ? 'rgba(63,185,80,0.3)' : 'var(--card-border)' }} />
                  {form.conteudo_texto && <div style={{ fontSize:11, color:'#3fb950', marginTop:4 }}>✅ Cifra pronta — pode transpor o tom após publicar</div>}
                </div>
              )}

              {/* Tom */}
              <div className="input-group">
                <label className="input-label">Tom original</label>
                <select className="input" value={form.tom_original} onChange={e => set('tom_original', e.target.value)} style={{ appearance:'none' }}>
                  <option value="">Não identificado</option>
                  {TONS.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>

            </div>{/* fim scroll */}

            {/* Botão salvar — fora do scroll para não sumir */}
            <div style={{ flexShrink:0, padding:'12px 0 4px', borderTop:'1px solid rgba(255,255,255,0.08)', marginTop:8 }}>
              <button onClick={salvar} disabled={salvando || !form.titulo.trim()}
                style={{ width:'100%', background: !form.titulo.trim() ? 'rgba(255,140,0,0.4)' : 'var(--brand2)', border:'none', color:'#fff', padding:15, fontSize:15, fontWeight:700, borderRadius:12, cursor: !form.titulo.trim() ? 'default' : 'pointer', fontFamily:'Montserrat,sans-serif' }}>
                {salvando ? <span className="spinner" style={{ width:20, height:20 }}/> : '🎸 Publicar cifra'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
